import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useCallback, useEffect, useState } from 'react';
import type { UpdateAccountBillingSettingsDto, AccountBillingSettingsDto, AresCompanyResult } from '@paladin-monorepo/common';
import { useAresCompanySearch } from '@/hooks/useAresCompanySearch';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { AlertCircle, Info, Plus, X } from 'lucide-react';
import { PayoutBankAccountsSection } from '@/components/billing/PayoutBankAccountsSection';

const schema = yup.object({
  legalName: yup.string().required(),
  registrationNumber: yup.string().required(),
  vatNumber: yup.string().nullable(),
  address: yup.object({
    street: yup.string().required(),
    city: yup.string().required(),
    zip: yup.string().required(),
    country: yup.string().required(),
  }),
  reverseCharge: yup.boolean().required(),
  billingContactName: yup.string().nullable(),
  billingContactEmail: yup.string().email().nullable(),
  billingContactPhone: yup.string().nullable(),
  additionalBillingEmails: yup.array().of(yup.string().email().required()).required(),
  paymentReminderDelivery: yup.string().oneOf(['billing_email', 'custom_email']).required(),
  customReminderEmail: yup.string().email().nullable(),
  paymentProvider: yup.string().nullable(),
  escrowConnected: yup.boolean().required(),
  escrowCurrency: yup.string().nullable(),
  payoutMethod: yup.string().oneOf(['bank_transfer', 'platform_wallet']).required(),
  invoiceDelivery: yup.string().oneOf(['billing_email', 'custom_email']).required(),
  customInvoiceEmail: yup.string().email().nullable(),
  invoiceFormat: yup.string().oneOf(['pdf', 'pdf_xml']).required(),
  separatePlatformFees: yup.boolean().required(),
  erpSystem: yup.string().required(),
  procurementPlatform: yup.string().required(),
  poRequired: yup.boolean().required(),
});

type BillingFormValues = yup.InferType<typeof schema>;

const COUNTRIES = [
  { value: 'CZ', label: 'Czech Republic' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
];

const PAYMENT_PROVIDERS = [
  { value: 'stripe', label: 'Stripe' },
  { value: 'adyen', label: 'Adyen' },
  { value: 'mangopay', label: 'Mangopay' },
  { value: 'other', label: 'Other' },
];

const CURRENCIES = [
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'CZK', label: 'CZK - Czech Koruna' },
];

const ERP_SYSTEMS = [
  { value: 'none', label: 'None' },
  { value: 'sap', label: 'SAP' },
  { value: 'oracle', label: 'Oracle' },
  { value: 'netsuite', label: 'NetSuite' },
  { value: 'quickbooks', label: 'QuickBooks' },
];

const PROCUREMENT_PLATFORMS = [
  { value: 'none', label: 'None' },
  { value: 'coupa', label: 'Coupa' },
  { value: 'ariba', label: 'Ariba' },
  { value: 'jaggaer', label: 'Jaggaer' },
];

function dtoToFormValues(dto: AccountBillingSettingsDto): BillingFormValues {
  return {
    legalName: dto.legalName,
    registrationNumber: dto.registrationNumber,
    vatNumber: dto.vatNumber ?? null,
    address: {
      street: dto.address.street,
      city: dto.address.city,
      zip: dto.address.zip,
      country: dto.address.country,
    },
    reverseCharge: dto.reverseCharge,
    billingContactName: dto.billingContactName ?? null,
    billingContactEmail: dto.billingContactEmail ?? null,
    billingContactPhone: dto.billingContactPhone ?? null,
    additionalBillingEmails: dto.additionalBillingEmails,
    paymentReminderDelivery: dto.paymentReminderDelivery,
    customReminderEmail: dto.customReminderEmail ?? null,
    paymentProvider: dto.paymentProvider ?? null,
    escrowConnected: dto.escrowConnected,
    escrowCurrency: dto.escrowCurrency ?? null,
    payoutMethod: dto.payoutMethod,
    invoiceDelivery: dto.invoiceDelivery,
    customInvoiceEmail: dto.customInvoiceEmail ?? null,
    invoiceFormat: dto.invoiceFormat,
    separatePlatformFees: dto.separatePlatformFees,
    erpSystem: dto.erpSystem,
    procurementPlatform: dto.procurementPlatform,
    poRequired: dto.poRequired,
  };
}

interface BillingSettingsFormProps {
  data: AccountBillingSettingsDto;
  onSubmit: (values: UpdateAccountBillingSettingsDto) => void;
  isPending: boolean;
}

export function BillingSettingsForm({ data, onSubmit, isPending }: BillingSettingsFormProps) {
  const { t } = useTranslation();

  const [newEmail, setNewEmail] = useState('');

  const {
    results: aresResults,
    setResults: setAresResults,
    isSearching: isAresSearching,
    search: searchAres,
    skipNextSearchRef,
  } = useAresCompanySearch({ debounceMs: 400 });

  const form = useForm<BillingFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      legalName: '',
      registrationNumber: '',
      vatNumber: null,
      address: { street: '', city: '', zip: '', country: 'CZ' },
      reverseCharge: false,
      billingContactName: null,
      billingContactEmail: null,
      billingContactPhone: null,
      additionalBillingEmails: [],
      paymentReminderDelivery: 'billing_email',
      customReminderEmail: null,
      paymentProvider: null,
      escrowConnected: false,
      escrowCurrency: null,
      payoutMethod: 'bank_transfer',
      invoiceDelivery: 'billing_email',
      customInvoiceEmail: null,
      invoiceFormat: 'pdf',
      separatePlatformFees: false,
      erpSystem: 'none',
      procurementPlatform: 'none',
      poRequired: false,
    },
  });

  useEffect(() => {
    if (data) {
      form.reset(dtoToFormValues(data));
    }
  }, [data, form]);

  const applyAresResult = (ares: AresCompanyResult) => {
    form.setValue('legalName', ares.obchodniJmeno);
    form.setValue('registrationNumber', ares.ico);
    form.setValue('vatNumber', ares.dic ?? null);
    const addr = [
      ares.sidlo.cisloDomovni,
      `${ares.sidlo.cisloOrientacni ?? ''}${ares.sidlo.cisloOrientacniPismeno ?? ''}`,
    ].filter(Boolean).join('/');
    form.setValue('address.street', `${ares.sidlo.nazevUlice ?? ''} ${addr}`.trim());
    form.setValue('address.city', ares.sidlo.nazevObce ?? '');
    form.setValue('address.zip', ares.sidlo.psc?.toString() ?? '');
    form.setValue('address.country', ares.sidlo.kodStatu ?? 'CZ');
    setAresResults([]);
  };

  const handleSubmit = (values: BillingFormValues) => {
    onSubmit(values as unknown as UpdateAccountBillingSettingsDto);
  };

  const handleAddEmail = () => {
    if (newEmail && newEmail.includes('@')) {
      const current = form.getValues('additionalBillingEmails') ?? [];
      form.setValue('additionalBillingEmails', [...current, newEmail]);
      setNewEmail('');
    }
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    const current = form.getValues('additionalBillingEmails') ?? [];
    form.setValue(
      'additionalBillingEmails',
      current.filter((e) => e !== emailToRemove),
    );
  };

  const paymentReminderDelivery = form.watch('paymentReminderDelivery');
  const payoutMethod = form.watch('payoutMethod');
  const invoiceDelivery = form.watch('invoiceDelivery');
  const escrowConnected = form.watch('escrowConnected');
  const additionalEmails = form.watch('additionalBillingEmails');

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="max-w-4xl space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('billing.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('billing.description')}</p>
        </div>

        {/* SECTION 1 - Legal Entity */}
        <Card>
          <CardHeader>
            <CardTitle>{t('billing.legalEntity.title')}</CardTitle>
            <CardDescription>{t('billing.legalEntity.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* ARES company search */}
            <div className="space-y-2">
              <Label>{t('billing.legalEntity.searchLabel')}</Label>
              <Combobox<AresCompanyResult>
                items={aresResults}
                itemToStringValue={(item: AresCompanyResult) => item.obchodniJmeno}
                onValueChange={(value: AresCompanyResult | null) => {
                  if (value) {
                    skipNextSearchRef.current = true;
                    applyAresResult(value);
                  }
                }}
                onInputValueChange={(inputValue: string) => {
                  searchAres(inputValue);
                }}
              >
                <ComboboxInput
                  placeholder={t('billing.legalEntity.searchPlaceholder')}
                  className="w-full"
                  showTrigger={false}
                />
                <ComboboxContent>
                  <ComboboxEmpty>
                    {isAresSearching
                      ? t('billing.legalEntity.searching', 'Hledání...')
                      : t('billing.legalEntity.noResults', 'Žádné výsledky')}
                  </ComboboxEmpty>
                  <ComboboxList>
                    {(_item) => {
                      const item = _item as AresCompanyResult;
                      return (
                        <ComboboxItem key={item.ico} value={item} className="flex-col items-start py-2">
                          <span className="text-xs text-muted-foreground">{item.ico}</span>
                          <span className="font-medium">{item.obchodniJmeno}</span>
                          {item.sidlo.textovaAdresa && (
                            <span className="text-xs text-muted-foreground">{item.sidlo.textovaAdresa}</span>
                          )}
                        </ComboboxItem>
                      );
                    }}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
              <FormField
                control={form.control}
                name="legalName"
                render={({ field }) => (
                  <FormItem className="sm:col-span-6">
                    <FormLabel>{t('billing.legalEntity.legalName')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="registrationNumber"
                render={({ field }) => (
                  <FormItem className="sm:col-span-3">
                    <FormLabel>{t('billing.legalEntity.registrationNumber')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="vatNumber"
                render={({ field }) => (
                  <FormItem className="sm:col-span-3">
                    <FormLabel>{t('billing.legalEntity.vatNumber')}</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ''} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="sm:col-span-6">
                <h4 className="text-sm font-medium mt-2">{t('billing.legalEntity.addressTitle')}</h4>
              </div>

              <FormField
                control={form.control}
                name="address.street"
                render={({ field }) => (
                  <FormItem className="sm:col-span-6">
                    <FormLabel>{t('billing.legalEntity.street')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.city"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>{t('billing.legalEntity.city')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.zip"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>{t('billing.legalEntity.postalCode')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.country"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>{t('billing.legalEntity.country')}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="reverseCharge"
                render={({ field }) => (
                  <FormItem className="sm:col-span-6 flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="flex items-center gap-1.5">
                      <FormLabel className="font-medium">{t('billing.legalEntity.reverseCharge')}</FormLabel>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          {t('billing.legalEntity.reverseChargeTooltip')}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* SECTION 2 - Billing Contact */}
        <Card>
          <CardHeader>
            <CardTitle>{t('billing.billingContact.title')}</CardTitle>
            <CardDescription>{t('billing.billingContact.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
              <FormField
                control={form.control}
                name="billingContactName"
                render={({ field }) => (
                  <FormItem className="sm:col-span-3">
                    <FormLabel>{t('billing.billingContact.name')}</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ''} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="billingContactEmail"
                render={({ field }) => (
                  <FormItem className="sm:col-span-3">
                    <FormLabel>{t('billing.billingContact.email')}</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} value={field.value ?? ''} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="billingContactPhone"
                render={({ field }) => (
                  <FormItem className="sm:col-span-3">
                    <FormLabel>{t('billing.billingContact.phone')}</FormLabel>
                    <FormControl>
                      <Input type="tel" {...field} value={field.value ?? ''} />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Additional billing emails */}
              <div className="sm:col-span-6 space-y-2">
                <Label>{t('billing.billingContact.additionalEmails')}</Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder={t('billing.billingContact.additionalEmailPlaceholder')}
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddEmail();
                      }
                    }}
                    className="max-w-md"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={handleAddEmail}>
                    <Plus className="h-4 w-4" />
                    {t('billing.billingContact.addEmail')}
                  </Button>
                </div>
                {additionalEmails && additionalEmails.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {additionalEmails.map((email, idx) => (
                      <Badge key={idx} variant="secondary" className="gap-1">
                        {email}
                        <button
                          type="button"
                          onClick={() => handleRemoveEmail(email)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Payment reminders delivery */}
              <div className="sm:col-span-6 space-y-3 border-t pt-4">
                <Label className="font-semibold">{t('billing.billingContact.remindersLabel')}</Label>
                <FormField
                  control={form.control}
                  name="paymentReminderDelivery"
                  render={({ field }) => (
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="billing_email"
                          checked={field.value === 'billing_email'}
                          onChange={field.onChange}
                          className="accent-primary"
                        />
                        <span className="text-sm">{t('billing.billingContact.billingEmail')}</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="custom_email"
                          checked={field.value === 'custom_email'}
                          onChange={field.onChange}
                          className="accent-primary"
                        />
                        <span className="text-sm">{t('billing.billingContact.customEmail')}</span>
                      </label>
                      {paymentReminderDelivery === 'custom_email' && (
                        <FormField
                          control={form.control}
                          name="customReminderEmail"
                          render={({ field: emailField }) => (
                            <FormItem className="ml-6 max-w-md">
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder={t('billing.billingContact.customEmailPlaceholder')}
                                  {...emailField}
                                  value={emailField.value ?? ''}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  )}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SECTION 3 - Payment Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>{t('billing.paymentConfig.title')}</CardTitle>
            <CardDescription>{t('billing.paymentConfig.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Escrow wallet status */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 rounded-lg border p-4 bg-muted/30">
              <div>
                <h4 className="text-sm font-semibold">{t('billing.paymentConfig.escrowStatus')}</h4>
                <div className="mt-2 flex items-center gap-3">
                  <Badge variant={escrowConnected ? 'success' : 'destructive'}>
                    {escrowConnected
                      ? t('billing.paymentConfig.connected')
                      : t('billing.paymentConfig.notConnected')}
                  </Badge>
                  {!escrowConnected && (
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <AlertCircle className="h-4 w-4 text-orange-400" />
                      {t('billing.paymentConfig.actionRequired')}
                    </span>
                  )}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => form.setValue('escrowConnected', !escrowConnected)}
              >
                {escrowConnected
                  ? t('billing.paymentConfig.manageConnection')
                  : t('billing.paymentConfig.connectProvider')}
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
              <FormField
                control={form.control}
                name="paymentProvider"
                render={({ field }) => (
                  <FormItem className="sm:col-span-3">
                    <FormLabel>{t('billing.paymentConfig.provider')}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value ?? undefined}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PAYMENT_PROVIDERS.map((p) => (
                          <SelectItem key={p.value} value={p.value}>
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="escrowCurrency"
                render={({ field }) => (
                  <FormItem className="sm:col-span-3">
                    <FormLabel>{t('billing.paymentConfig.escrowCurrency')}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value ?? undefined}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>

            {/* Payout method */}
            <div className="space-y-3 border-t pt-4">
              <Label className="font-semibold">{t('billing.paymentConfig.payoutMethod')}</Label>
              <FormField
                control={form.control}
                name="payoutMethod"
                render={({ field }) => (
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="bank_transfer"
                        checked={field.value === 'bank_transfer'}
                        onChange={field.onChange}
                        className="accent-primary"
                      />
                      <div>
                        <span className="text-sm font-medium">{t('billing.paymentConfig.bankTransfer')}</span>
                        <p className="text-xs text-muted-foreground">{t('billing.paymentConfig.bankTransferDesc')}</p>
                      </div>
                    </label>
                    {payoutMethod === 'bank_transfer' && (
                      <PayoutBankAccountsSection
                        accountId={data.accountId}
                        accounts={data.payoutBankAccounts ?? []}
                        defaultHolderName={data.legalName}
                      />
                    )}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="platform_wallet"
                        checked={field.value === 'platform_wallet'}
                        onChange={field.onChange}
                        className="accent-primary"
                      />
                      <div>
                        <span className="text-sm font-medium">{t('billing.paymentConfig.platformWallet')}</span>
                        <p className="text-xs text-muted-foreground">{t('billing.paymentConfig.platformWalletDesc')}</p>
                      </div>
                    </label>
                  </div>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* SECTION 4 - Invoice Preferences */}
        <Card>
          <CardHeader>
            <CardTitle>{t('billing.invoicePrefs.title')}</CardTitle>
            <CardDescription>{t('billing.invoicePrefs.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Invoice delivery */}
            <div className="space-y-3">
              <Label className="font-semibold">{t('billing.invoicePrefs.deliveryLabel')}</Label>
              <FormField
                control={form.control}
                name="invoiceDelivery"
                render={({ field }) => (
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="billing_email"
                        checked={field.value === 'billing_email'}
                        onChange={field.onChange}
                        className="accent-primary"
                      />
                      <span className="text-sm">{t('billing.billingContact.billingEmail')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="custom_email"
                        checked={field.value === 'custom_email'}
                        onChange={field.onChange}
                        className="accent-primary"
                      />
                      <span className="text-sm">{t('billing.billingContact.customEmail')}</span>
                    </label>
                    {invoiceDelivery === 'custom_email' && (
                      <FormField
                        control={form.control}
                        name="customInvoiceEmail"
                        render={({ field: emailField }) => (
                          <FormItem className="ml-6 max-w-md">
                            <FormControl>
                              <Input
                                type="email"
                                placeholder={t('billing.billingContact.customEmailPlaceholder')}
                                {...emailField}
                                value={emailField.value ?? ''}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                )}
              />
            </div>

            {/* Separate platform fees */}
            <FormField
              control={form.control}
              name="separatePlatformFees"
              render={({ field }) => (
                <div className="flex items-center justify-between border-t pt-6 max-w-2xl">
                  <div className="space-y-0.5">
                    <Label>{t('billing.invoicePrefs.separateFees')}</Label>
                    <FormDescription>
                      {t('billing.invoicePrefs.separateFeesDesc')}
                    </FormDescription>
                  </div>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              )}
            />
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="ghost"
            onClick={() => form.reset(dtoToFormValues(data))}
          >
            {t('billing.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? t('billing.saving') : t('billing.save')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
